// eslint-disable-next-line no-restricted-imports
import { useLocation, useParams } from 'react-router'
import { useCallback } from 'react'
import { useNavigateWithSearch } from 'infrastructure/routing'
import { useSearchParams } from 'shared/hooks'
import { useProductForVanityUrl } from '../../../api/vanityUrls.atoms'
import * as Sentry from '@sentry/react'

type RoutingParams = {
  treeId?: string
  version?: string
  nodeId?: string
}

export function useTreeIdRouting() {
  const { treeVersionNodeId } = useParams()
  const navigate = useNavigateWithSearch()
  const { pathname } = useLocation()
  const [search] = useSearchParams()
  return useCallback(
    ({ treeId, version, nodeId }: RoutingParams) => {
      if (treeVersionNodeId) {
        // We're already on an id route, don't append to it
        return
      }
      const searchParams = new URLSearchParams(search)
      const parts = ['', ...pathname.split('/').filter((s) => !!s)]
      if (treeId && version) {
        let idPart = `${treeId}:${version}`
        if (nodeId) {
          searchParams.set('nodeId', nodeId)
        } else {
          searchParams.delete('nodeId')
        }
        parts.push(idPart)
        navigate({ pathname: parts.join('/'), search: searchParams.toString() })
      }
    },
    [pathname, navigate, treeVersionNodeId, search],
  )
}

function useGetProductFromVanityUrl() {
  const getProduct = useProductForVanityUrl()
  return useCallback(
    (vanityUrl: string) => {
      const currMarker = '/curriculum/'
      const nodeMarker = '/node/'
      let vanityId = vanityUrl
      if (vanityUrl.includes(currMarker)) {
        vanityId = vanityUrl.split(currMarker).pop()!
        if (vanityId.includes(nodeMarker)) {
          vanityId = vanityId.split(nodeMarker)[0]
        }
      }
      return getProduct(vanityId)
    },
    [getProduct],
  )
}

export function useIdsFromPastedString() {
  const getProduct = useGetProductFromVanityUrl()
  return useCallback(
    (value: string) => {
      // Decode URL-encoded characters (e.g., %3A -> :) at user input boundary
      let decodedValue = value
      try {
        decodedValue = decodeURIComponent(value)
      } catch (error) {
        // Log malformed URIs to Sentry for monitoring
        Sentry.captureException(error, {
          extra: {
            input: value,
            context: 'useIdsFromPastedString - malformed URI',
          },
        })
        // If decoding fails, use original value
      }

      const product = getProduct(decodedValue)
      if (product) {
        return {
          treeId: product.tree?.tree_id,
          version: product.tree?.version,
        }
      }

      const withoutSearch = decodedValue.split('?').reverse().pop()
      if (!withoutSearch) return {}
      let nodeId = ''
      if (withoutSearch.includes('node/')) {
        nodeId = withoutSearch.split('node/').pop() ?? ''
      }
      const idPart = withoutSearch
        .split('/')
        .reverse()
        .find((s) => s.includes(':'))
      if (!idPart) return {}
      const [treeId, version, treeNodeId] = idPart.split(':')
      return { treeId, version, nodeId: treeNodeId || nodeId }
    },
    [getProduct],
  )
}
